// UI strings of the AI assistant screen (Chat, MessageBubble, chat hooks), English only.
// Merged into the i18next resources in ../i18n.js. Use as t("assistant.<key>"); outside React use i18n.t(...).

const en = {
	title: "ECO-READY Assistant",
	conversations: "Conversations",
	newConversation: "New conversation",
	openConversations: "Open conversations",
	closeConversations: "Close conversations",
	conversationOptions: "Conversation options",
	chatMenu: "Chat menu",
	loading: "Loading...",
	loadingMore: "Loading more...",
	noConversations: "No conversations",
	noMatches: "No matching conversations",
	conversationsLoadFailed: "Could not load conversations",
	conversationOpenFailed: "Could not open this conversation",
	searchConversations: "Search conversations",
	retry: "Retry",
	rename: "Rename",
	renameLabel: "Conversation name",
	renameSave: "Save name",
	renameCancel: "Cancel rename",
	delete: "Delete",
	deleteTitle: "Delete conversation",
	deleteText: "Are you sure you want to delete this conversation? This action cannot be undone.",
	cancel: "Cancel",
	conversationDeleted: "Conversation deleted",
	conversationDeleteFailed: "Failed to delete conversation",
	conversationRenameFailed: "Failed to rename conversation",
	messagesLabel: "Conversation messages",
	messageInput: "Message",
	send: "Send message",
	stop: "Stop generating",
	jumpToBottom: "Jump to bottom",
	today: "Today",
	yesterday: "Yesterday",
	youSaid: "You said",
	assistantSaid: "Assistant said",
	generating: "Generating response",
	sendFailed: "Failed to send message",
	newResponse: "New response",
	group: {
		today: "Today",
		yesterday: "Yesterday",
		week: "Previous 7 days",
		older: "Older",
	},
	filter: {
		label: "Show conversations by assistant",
		all: "All",
	},
	export: {
		copyMarkdown: "Copy as Markdown",
		downloadMarkdown: "Download as Markdown",
		copied: "Conversation copied",
		failed: "Could not copy the conversation",
		you: "You",
		assistant: "Assistant",
		file: "conversation",
	},
	readOnlyEvaluation: "Archived report evaluation. Start a new chat to continue.",
	welcome: {
		lockNote: "Mode and model are fixed once you send your first message.",
		wise: {
			title: "Welcome to EcoAssistant",
			intro: "Ask questions grounded in ECO-READY Observatory data, including:",
			outro: "Ask me anything about the Observatory's data.",
		},
		bare: {
			title: "Welcome to LLM mode",
			intro: "Answers come from the language model's general knowledge.",
		},
	},
	topic: {
		market: "EU market prices and production",
		livingLabs: "Living Lab sensors, weather and crops",
		sustainability: "Sustainability: life cycle assessment and social impact",
		foodSafety: "Food safety and contaminants",
		climate: "Climate and disaster risk",
		policy: "Food policy (FAPDA) and research reports",
	},
	model: {
		primary: "Model 1",
		secondary: "Model 2",
		notConfigured: "Not configured",
	},
	choice: {
		label: "Assistant and model",
		current: "Mode:",
		option: "{{mode}} ({{tier}})",
		tag: "{{mode}} · {{tier}}",
		switched: "Switched to {{choice}}: new chat started",
		lockedNote: "Changing this starts a new chat",
		lockedWait: "Wait for the answer to finish",
	},
	mode: {
		wise: "EcoAssistant",
		bare: "LLM",
		evaluation: "Evaluation",
		wiseDescription: "Answers grounded in ECO-READY Observatory data",
		bareDescription: "General knowledge only, no Observatory data",
		wisePlaceholder: "Ask EcoAssistant about ECO-READY Observatory data...",
		barePlaceholder: "Ask a question...",
		bareNotice: "General knowledge only. Answers are not based on ECO-READY Observatory data.",
	},
	message: {
		copy: "Copy message",
		copied: "Copied!",
		copyCode: "Copy code",
		copyTable: "Copy table as CSV",
		sources: "Sources",
		sourceNamed: "Source: {{name}}",
		sourceDefault: "Source {{index}}",
		dataUsed: "Data used",
		opensNewTab: "(opens in new tab)",
	},
	tool: {
		running: "running",
		done: "done",
		failed: "failed",
		showDetails: "Show details",
		summary_one: "Used {{count}} data source",
		summary_other: "Used {{count}} data sources",
		names: {
			compare_data: "Compared data",
			describe_dataset: "Looked up dataset details",
			get_available_values: "Checked available values",
			get_statistics: "Calculated statistics",
			get_top_grouped: "Ranked groups",
			list_all_keyspaces: "Browsed data areas",
			list_countries: "Listed countries",
			list_datasets: "Listed datasets",
			query_data: "Queried data",
			search_knowledge_base: "Searched the knowledge base",
		},
	},
	status: {
		stopped: "Stopped",
		stoppedEmpty: "Stopped before any output",
		paused: "Paused: the tool-call limit was reached",
		disconnected: "Connection lost",
		failed: "Response failed",
		failedReason: "Response failed: {{reason}}",
		unknownError: "unknown error",
		continue: "Continue",
		working: "Working...",
		elapsed: "{{count}}s",
	},
	chart: {
		failed: "Could not render this chart",
		generating: "Working...",
		label: "Chart",
		viewData: "View data",
		hideData: "Hide data",
	},
	report: {
		actions: "Report actions",
		generate: "Generate report",
		evaluate: "Evaluate report",
		viewEvaluation: "View evaluation",
		needsReport: "Generate a report first",
		requested: "Report requested",
		requestedAt: "Report requested · {{time}}",
		badge: "Report",
		download: "Download",
		downloadMarkdown: "Markdown (.md)",
		downloadPdf: "PDF",
		file: "report",
	},
	upload: {
		button: "Upload a report",
		tooltip: "Upload a report (PDF, TXT, MD, DOC, DOCX, up to 10 MB)",
		formats: "PDF, TXT, MD, DOC or DOCX, up to 10 MB",
		uploading: "Reading the report...",
		done: "Report uploaded: {{name}}. You can evaluate it or ask about it.",
		label: "Uploaded report",
		pages_one: "{{count}} page",
		pages_other: "{{count}} pages",
		chars_one: "{{n}} character",
		chars_other: "{{n}} characters",
		showFull: "Show full text",
		hideFull: "Hide full text",
		exportLine: "Uploaded report: {{name}}",
		exportLinePages_one: "Uploaded report: {{name}} ({{count}} page)",
		exportLinePages_other: "Uploaded report: {{name}} ({{count}} pages)",
		errors: {
			unsupported_type: "This file type is not supported. Upload a PDF, TXT, MD, DOC or DOCX file.",
			file_too_large: "This file is too large. The limit is {{limit}} MB.",
			empty_file: "This file is empty.",
			report_too_long: "This report is too long: {{chars}} characters, and the limit is {{limit}}. Upload a shorter report; nothing is cut off.",
			report_too_long_plain: "This report is too long. Upload a shorter report; nothing is cut off.",
			conversation_full: "This conversation already holds as much uploaded text as it can: start a new conversation to upload another report.",
			too_many_uploads: "You are uploading too fast. Wait a moment and try again.",
			busy: "The server is busy reading other reports. Try again in a few seconds.",
			no_text: "No readable text was found in this file. Scanned documents are not supported.",
			unreadable: "This file could not be read. It may be damaged or password-protected.",
			converter_unavailable: "DOC files cannot be read on this server right now. Save the file as DOCX or PDF and upload that.",
			report_not_available: "Reports cannot be uploaded to an archived evaluation chat. Start a new conversation.",
			mode_mismatch: "This conversation uses another assistant. Start a new conversation to upload this report.",
			model_mismatch: "This conversation uses another model. Start a new conversation to upload this report.",
			model_not_configured: "The selected model is not available. Pick another model and try again.",
			conversation_not_found: "This conversation no longer exists. Start a new conversation and try again.",
			session_expired: "Your session has expired. Sign in again and retry.",
			no_file: "No file was selected.",
			network: "The report could not be sent because the server cannot be reached. Check your connection and try again.",
			generic: "The report could not be uploaded. Please try again.",
		},
	},
	evaluation: {
		title: "Report evaluation: {{title}}",
		titleFallback: "Report evaluation",
		close: "Close evaluation",
		running: "The judge is fact-checking the report. You can close this panel; the result will appear on the report.",
		finished: "Evaluation finished: {{total}} of 20",
		failed: "The evaluation failed: {{reason}}",
		stale: "it did not finish in time",
		reevaluate: "Re-evaluate",
		startFailed: "Could not start the evaluation: {{reason}}",
		overall: "Overall score",
		criteria: {
			data_accuracy: "Data accuracy",
			cross_dataset: "Cross-dataset integration",
			hallucination: "Hallucination control",
			actionability: "Actionability",
		},
		control: {
			poor: "Poor",
			weak: "Weak",
			fair: "Fair",
			good: "Good",
			strong: "Strong",
		},
		scoreText: "{{label}}: {{score}} of {{max}}",
		controlText: "{{label}}: {{score}} of {{max}} ({{level}})",
		assessment: "Assessment",
		claimChecks: "Claim checks",
		claimChecksCounts: "Claim checks ({{verified}} verified · {{contradicted}} contradicted · {{unverifiable}} unverifiable)",
		chipRunning: "Evaluating…",
		chipScore: "Evaluation {{total}}/20",
		chipFailed: "Evaluation failed",
	},
};


export const assistantResources = { en };
